import type { Workspace } from '~/types/workspace';

export type { Workspace };

type Listener = () => void;

const STORAGE_KEY = 'dg_current_workspace_id';

let workspaces: Workspace[] = [];
let currentWorkspaceId: number | null = null;
/** True after first fetchWorkspaces attempt finishes (success or empty/error). */
let bootstrapReady = false;
/** Human-readable bootstrap error for UI feedback when initial fetch fails. */
let bootstrapError: string | null = null;
const listeners = new Set<Listener>();

function notify() {
  listeners.forEach((l) => l());
}

export const workspaceStore = {
  getWorkspaces: (): Workspace[] => workspaces,

  getCurrentWorkspaceId: (): number | null => currentWorkspaceId,

  setCurrentWorkspaceId: (id: number | null) => {
    currentWorkspaceId = id;
    if (id !== null) {
      localStorage.setItem(STORAGE_KEY, String(id));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    notify();
  },

  isBootstrapReady: (): boolean => bootstrapReady,

  getBootstrapError: (): string | null => bootstrapError,

  setBootstrapReady: (ready: boolean) => {
    bootstrapReady = ready;
    notify();
  },

  setBootstrapError: (error: string | null) => {
    bootstrapError = error;
    notify();
  },

  /**
   * Sets list and picks current workspace: stored id if valid, else first item (API order: newest first).
   */
  loadWorkspaces: (list: Workspace[]) => {
    workspaces = list;
    bootstrapError = null;
    const activeList = list.filter((w) => w.status === 'ACTIVE');
    const stored = localStorage.getItem(STORAGE_KEY);
    const storedId = stored ? parseInt(stored, 10) : NaN;
    if (!Number.isNaN(storedId) && activeList.some((w) => w.id === storedId)) {
      currentWorkspaceId = storedId;
    } else if (activeList.length > 0) {
      currentWorkspaceId = activeList[0].id;
      localStorage.setItem(STORAGE_KEY, String(currentWorkspaceId));
    } else {
      currentWorkspaceId = null;
      localStorage.removeItem(STORAGE_KEY);
    }
    notify();
  },

  /** After 404 on a workspace-scoped resource: clear invalid selection and re-sync from list. */
  invalidateCurrentIfMatches: (invalidId: number) => {
    if (currentWorkspaceId === invalidId) {
      const activeList = workspaces.filter((w) => w.status === 'ACTIVE');
      currentWorkspaceId = activeList[0]?.id ?? null;
      if (currentWorkspaceId !== null) {
        localStorage.setItem(STORAGE_KEY, String(currentWorkspaceId));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
      notify();
    }
  },

  subscribe: (listener: Listener) => {
    listeners.add(listener);
    
return () => {
      listeners.delete(listener);
    };
  },

  reset: () => {
    workspaces = [];
    currentWorkspaceId = null;
    bootstrapReady = false;
    bootstrapError = null;
    localStorage.removeItem(STORAGE_KEY);
    notify();
  },
};
